import type { ISearchState } from '../types.ts'
import { DEFAULT_RECORDS_PER_PAGE, DEFAULT_EXPORT_FIELDS, COLLECTIONS_SEARCH_TAB, STANDARDS_SEARCH_TAB, UPDATES_SEARCH_TAB, SEARCH_IN_OPTIONS,
  STANDARDS_SEARCH_SORT_OPTIONS, COLLECTIONS_SEARCH_SORT_OPTIONS, UPDATES_SEARCH_SORT_OPTIONS, STANDARDS_SEARCH_SORT_DEFAULT, COLLECTIONS_SEARCH_SORT_DEFAULT,
  UPDATES_SEARCH_SORT_DEFAULT } from '@/components/Search/constants.ts';
import { getYearDateRange } from '@/utils/dateHelpers.ts';

const getYearOptionsDesc = (startYear = 1927) => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
    const year = currentYear - i; // descending
    return { id: String(year), text: String(year) };
  });
}
const { start: oneYearAgo, end: today } = getYearDateRange();

const getLanguageOptions = () => {
  return [
    { id: "english", text: "english"},
    { id: "french", text: "french"},
    { id: "german", text: "german"},
    { id: "italian", text: "italian"},
    { id: "spanish", text: "spanish"},
    { id: "russian", text: "russian"},
    { id: "chinese", text: "chinese"},
    { id: "portuguese", text: "portuguese"}
  ];
}


const initialState: ISearchState = {
  "activeCollectionId": '',
  "activeCollectionName": '',
  "displaySearchBar": false,
  "activeTab": "standards",
  "searchResultActive": false, //search result view is active
  "nonce": '',
  "filterDefaults": {},
  "recentlyViewedDocs": [],

  //standards search states
  "standards.allowAdvanceSearch": true,
  "standards.displayMoreSearchBarFilters": true,
  "standards.mobileAdvanceSearch": true,
  "standards.documentNumber": "",
  "standards.publisher": [],
  "standards.status": [],
  "standards.country": [],
  "standards.dataset": [],
  "standards.crossReference": "",
  "standards.icsCode": "",
  "standards.descriptorField": [],
  "standards.descriptorGroup": [],
  "standards.descriptorSubGroup": [],
  "standards.language": [],
  "standards.year": [],
  "standards.searchIn":  [{ "id": "0", "text": "all"}],
  "standards.resultData": {},
  "standards.result.sort": [STANDARDS_SEARCH_SORT_DEFAULT],
  "standards.result.page": 1,
  "standards.result.loading": false,
  "standards.savedSearches": [],

  //standards search facets states
  "standards.facetFilter.publisher": [],
  "standards.facetFilter.status": [],
  "standards.facetFilter.country": [],
  "standards.facetFilter.dataset": [],
  "standards.facetFilter.crossReference": "",
  "standards.facetFilter.icsCode": "",
  "standards.facetFilter.descriptorField": [],
  "standards.facetFilter.descriptorGroup": [],
  "standards.facetFilter.descriptorSubGroup": [],
  "standards.facetFilter.language": [],
  "standards.facetFilter.year": [],
  "standards.facetFilter.searchIn": [{ "id": "0", "text": "all"}],
  "standards.facetFilter.searchIn.expanded": false,

  //collections export status
  "standards.export.selectedFields": [...DEFAULT_EXPORT_FIELDS[STANDARDS_SEARCH_TAB]],
  "standards.export.format": 'csv',
  "standards.export.status": '',
  "standards.export.totalFound": 0,

  //collections search states
  "collections.allowAdvanceSearch": true,
  "collections.displayMoreSearchBarFilters": true,
  "collections.mobileAdvanceSearch": true,
  "collections.documentNumber": "",
  "collections.publisher": [],
  "collections.collections": [],
  "collections.companyCollection": [],
  "collections.documentType": [],
  "collections.status": [],
  "collections.tags": [],
  "collections.notes": "",
  "collections.icsCode": "",
  "collections.descriptorField": [],
  "collections.descriptorGroup": [],
  "collections.descriptorSubGroup": [],
  "collections.resultData": {},
  "collections.result.sort": [COLLECTIONS_SEARCH_SORT_DEFAULT],
  "collections.result.page": 1,
  "collections.result.loading": false,

  //collections search facets states
  "collections.facetFilter.publisher": [],
  "collections.facetFilter.collections": [],
  "collections.facetFilter.companyCollection": [],
  "collections.facetFilter.documentType": [],
  "collections.facetFilter.status": [],
  "collections.facetFilter.tags": [],
  "collections.facetFilter.notes": "",
  "collections.facetFilter.icsCode": "",
  "collections.facetFilter.descriptorField": [],
  "collections.facetFilter.descriptorGroup": [],
  "collections.facetFilter.descriptorSubGroup": [],

  //collections export status
  "collections.export.selectedFields": [...DEFAULT_EXPORT_FIELDS[COLLECTIONS_SEARCH_TAB]],
  "collections.export.format": 'csv',
  "collections.export.status": '',
  "collections.export.totalFound": 0,

  //updates search states
  "updates.result.sort": [UPDATES_SEARCH_SORT_DEFAULT],
  "updates.result.page": 1,
  "updates.result.loading": false,
  "updates.result.recordsPerPage": DEFAULT_RECORDS_PER_PAGE,
  "updates.resultData": {},
  "updates.what": [],
  "updates.publisher": [],
  "updates.when": [],
  "updates.dataset": [],
  "updates.dateStart": oneYearAgo,
  "updates.dateEnd": today,

  //updates search facets states
  "updates.facetFilter.what": [],
  "updates.facetFilter.publisher": [],
  "updates.facetFilter.when": [],
  "updates.facetFilter.dataset": [],
  // set oneYearAgo - 1 day for dateStart default
  "updates.facetFilter.dateStart": null,
  "updates.facetFilter.dateEnd": null,

  //updates export status
  "updates.export.selectedFields": [...DEFAULT_EXPORT_FIELDS[UPDATES_SEARCH_TAB]],
  "updates.export.format": 'csv',
  "updates.export.status": '',
  "updates.export.totalFound": 0,



  //lookup options
  "lookUpOptions.standards.publisher": [],
  "lookUpOptions.standards.status": [],
  "lookUpOptions.standards.dataset": [],
  "lookUpOptions.standards.language": getLanguageOptions(),
  "lookUpOptions.standards.year": getYearOptionsDesc(),
  "lookUpOptions.standards.country": [],
  "lookUpOptions.standards.descriptorField": [],
  "lookUpOptions.standards.descriptorGroup": [],
  "lookUpOptions.standards.descriptorSubGroup": [],
  "lookUpOptions.standards.searchIn": SEARCH_IN_OPTIONS,
  "lookUpOptions.standards.sortOptions": STANDARDS_SEARCH_SORT_OPTIONS,

  //facets lookupoptions for standard search, set dynamically when search is performed
  "lookUpOptions.standards.facetFilter.publisher": [],
  "lookUpOptions.standards.facetFilter.status": [],
  "lookUpOptions.standards.facetFilter.country": [],
  "lookUpOptions.standards.facetFilter.dataset": [],
  "lookUpOptions.standards.facetFilter.crossReference": "",
  "lookUpOptions.standards.facetFilter.descriptorField": [],
  "lookUpOptions.standards.facetFilter.descriptorGroup": [],
  "lookUpOptions.standards.facetFilter.descriptorSubGroup": [],
  "lookUpOptions.standards.facetFilter.language": [],
  "lookUpOptions.standards.facetFilter.year": [],
  "lookUpOptions.standards.facetFilter.searchIn": [],

  "lookUpOptions.collections.documentType": [],
  "lookUpOptions.collections.collections": [],
  "lookUpOptions.collections.companyCollection": [],
  "lookUpOptions.collections.tags": [],
  "lookUpOptions.collections.sortOptions": COLLECTIONS_SEARCH_SORT_OPTIONS,

  //facets lookupoptions for collection search, set dynamically when search is performed
  "lookUpOptions.collections.facetFilter.status": [],
  "lookUpOptions.collections.facetFilter.dataset": [],
  "lookUpOptions.collections.facetFilter.publisher": [],
  "lookUpOptions.collections.facetFilter.documentType": [],
  "lookUpOptions.collections.facetFilter.collections": [],
  "lookUpOptions.collections.facetFilter.companyCollection": [],
  "lookUpOptions.collections.facetFilter.tags": [],
  "lookUpOptions.collections.facetFilter.descriptorField": [],
  "lookUpOptions.collections.facetFilter.descriptorGroup": [],
  "lookUpOptions.collections.facetFilter.descriptorSubGroup": [],

  "lookUpOptions.updates.what": [],
  "lookUpOptions.updates.publisher": [],
  "lookUpOptions.updates.when": [],
  "lookUpOptions.updates.dataset": [],
  "lookUpOptions.updates.facetFilter.what": [],
  "lookUpOptions.updates.facetFilter.publisher": [],
  "lookUpOptions.updates.facetFilter.when": [],
  "lookUpOptions.updates.facetFilter.dataset": [],
  "lookUpOptions.updates.sortOptions": UPDATES_SEARCH_SORT_OPTIONS,
  "lookUpOptions.allDescriptorField": [],
  "lookUpOptions.allDescriptorGroup": [],
  "lookUpOptions.allDescriptorSubGroup": []
}

export default initialState
